import {mapRecordToItems} from '@/helpers/mapRecordToItems.helper';
import store from '@/store';
import {LoginTypes} from '@/enums/LoginTypes.enum';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {CrudActionTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudActionTypes';
import {CustomAccessKeys} from '@/classes/clientOnly/permissionTreeResources/enums/CustomAccessKeys';

/** Recruiting-Auswertung: Leserecht auf alle Mitarbeitenden inklusive Bewerbung (wie stat-manager recruiting.access.ts) */
export function canReadRecruitingOverview(): boolean {
    const cam = store.state.crudAccessManager;
    return !!cam && cam.getAccessibleEntities(CrudEntityTypes.EMPLOYEE, CrudActionTypes.READ) === 'all'
        && cam.canReadAllProvided(CrudEntityTypes.EMPLOYEE, 'vanillaApplication');
}

export enum NavigationDrawerGroups {
    ACCOUNTING = 'ACCOUNTING',
    PROFILES = 'PROFILES',
    CATALOGUE = 'CATALOGUE',
    EVENTS = 'EVENTS',
    QUALITY_MANAGEMENT = 'QUALITY_MANAGEMENT',
    ACQUISITION_LISTS = 'ACQUISITION_LISTS',
    AGENCY_VIEW = 'AGENCY_VIEW',
    PLANNING = 'PLANNING',
    TOOLS = 'TOOLS',
    JOB_ADS = 'JOB_ADS',
    TEMPLATES = 'TEMPLATES',
    WAREHOUSE = 'WAREHOUSE',
    OTHER = 'OTHER',
}

export namespace NavigationDrawerGroups {
    type Data = {
        text: string;
        icon: string;
    }

    export const Record: Record<NavigationDrawerGroups, Data> = {
        [NavigationDrawerGroups.PROFILES]: {
            text: 'Profile',
            icon: 'mdi-account-group',
        },
        [NavigationDrawerGroups.EVENTS]: {
            text: 'Veranstaltungen',
            icon: 'mdi-calendar',
        },
        [NavigationDrawerGroups.CATALOGUE]: {
            text: 'Produktkatalog',
            icon: 'mdi-book-open-page-variant',
        },
        [NavigationDrawerGroups.ACCOUNTING]: {
            text: 'Buchhaltung',
            icon: 'mdi-account-cash',
        },
        [NavigationDrawerGroups.QUALITY_MANAGEMENT]: {
            text: 'Qualitätsmanagement',
            icon: 'mdi-poll',
        },
        [NavigationDrawerGroups.ACQUISITION_LISTS]: {
            text: 'Akquise-Listen',
            icon: 'mdi-room-service',
        },
        [NavigationDrawerGroups.AGENCY_VIEW]: {
            text: 'Ansicht als Agentur',
            icon: 'mdi-eye-outline',
        },
        [NavigationDrawerGroups.PLANNING]: {
            text: 'Planung',
            icon: 'mdi-clipboard-text',
        },
        [NavigationDrawerGroups.TOOLS]: {
            text: 'Werkzeuge',
            icon: 'mdi-tools',
        },
        [NavigationDrawerGroups.JOB_ADS]: {
            text: 'Stellenanzeigen',
            icon: 'mdi-account-tie',
        },
        [NavigationDrawerGroups.WAREHOUSE]: {
            text: 'Logistik',
            icon: 'mdi-warehouse',
        },
        [NavigationDrawerGroups.TEMPLATES]: {
            text: 'Vorlagen',
            icon: 'mdi-file-document',
        },
        [NavigationDrawerGroups.OTHER]: {
            text: 'Sonstige',
            icon: 'mdi-dots-horizontal',
        },
    }

    export const Items = mapRecordToItems(Record);
}

export enum NavigationDrawerAccess {
    // OPTIMIZATIONS = 'OPTIMIZATIONS',
    ACQUISITION_LISTS_TABLE = 'ACQUISITION_LISTS_TABLE',
    ACQUISITION_LIST_OVERVIEW = 'ACQUISITION_LIST_OVERVIEW',
    ACTIVITIES = 'ACTIVITIES',
    ACTIVITY_REGISTRATIONS = 'ACTIVITY_REGISTRATIONS',
    APPLICANTS_LANDING_PAGE_OVERVIEW = 'APPLICANTS_LANDING_PAGE_OVERVIEW',
    BOOKING_FORM_TEMPLATES = 'BOOKING_FORM_TEMPLATES',
    CATALOGUE_PRODUCTS = 'CATALOGUE_PRODUCTS',
    CATALOGUE_SCHEDULED_PRODUCT_SERIES = 'CATALOGUE_SCHEDULED_PRODUCT_SERIES',
    COURSES = 'COURSES',
    DASHBOARD = 'DASHBOARD',
    EMAIL_TEMPLATES = 'EMAIL_TEMPLATES',
    EMPLOYEES = 'EMPLOYEES',
    EMPLOYEE_CONTRACT_TEMPLATES = 'EMPLOYEE_CONTRACT_TEMPLATES',
    INSTITUTIONS = 'INSTITUTIONS',
    INVOICES = 'INVOICES',
    INVOICE_CYCLES = 'INVOICE_CYCLES',
    INVOICE_TEMPLATES = 'INVOICE_TEMPLATES',
    MAKEUP_REFUNDS = 'MAKEUP_REFUNDS',
    ROOM_RENT = 'ROOM_RENT',
    JOB_ADS_APPLICANTS = 'JOB_ADS_APPLICANTS',
    RECRUITING_OVERVIEW = 'RECRUITING_OVERVIEW',
    JOB_ADS_OVERVIEW = 'JOB_ADS_OVERVIEW',
    MY_APPOINTMENTS = 'MY_APPOINTMENTS',
    MY_PROFILE = 'MY_PROFILE',
    MY_SCHEDULE = 'MY_SCHEDULE',
    PARENTS = 'PARENTS',
    PAYOFF = 'PAYOFF',
    PAYOFF_AUDIT = 'PAYOFF_AUDIT',
    PLANNING_HANDOUT_EVENTS = 'PLANNING_HANDOUT_EVENTS',
    PLANNING_HANDOUT_FEEDBACK = 'PLANNING_HANDOUT_FEEDBACK',
    PLANNING_HANDOUT_OVERVIEW = 'PLANNING_HANDOUT_OVERVIEW',
    PLANNING_OPEN_INSTITUTIONS = 'PLANNING_OPEN_INSTITUTIONS',
    PRODUCT_ORDERS = 'PRODUCT_ORDERS',
    PRODUCT_SHIPMENTS = 'PRODUCT_SHIPMENTS',
    PRODUCT_SHIPMENT_BUNDLES = 'PRODUCT_SHIPMENT_BUNDLES',
    /** Old menu key "Standorte – QM": grants the QM-Cockpit (no own menu entry any more) */
    QUALITY_MANAGEMENT_INSTITUTIONS = 'QUALITY_MANAGEMENT_INSTITUTIONS',
    /** Old menu key "Lehrkräfte – QM": grants the QM-Cockpit (no own menu entry any more) */
    QUALITY_MANAGEMENT_TEACHERS = 'QUALITY_MANAGEMENT_TEACHERS',
    /** QM-Cockpit; also implied by the two old QM keys, so existing roles keep working */
    QUALITY_MANAGEMENT_COCKPIT = 'QUALITY_MANAGEMENT_COCKPIT',
    SETTINGS = 'SETTINGS',
    STATS = 'STATS',
    STUDENTS = 'STUDENTS',
    SUBJECTS = 'SUBJECTS',
    SUBSTITUTION_INQUIRIES = 'SUBSTITUTION_INQUIRIES',
    SUBSTITUTION_INQUIRIES_TABLE = 'SUBSTITUTION_INQUIRIES_TABLE',
    /** Online make-up rounds (Saturday); the view also needs the custom key MANAGE_MAKEUP_ROUNDS */
    MAKEUP_ROUNDS = 'MAKEUP_ROUNDS',
    SUBSTITUTION_PLAN = 'SUBSTITUTION_PLAN',
    /** Monitoring of the takeover offers; also implied by SUBSTITUTION_INQUIRIES_TABLE (see getEffectiveAccess) */
    TAKEOVER_OFFERS = 'TAKEOVER_OFFERS',
    TAXONOMIES = 'TAXONOMIES',
    TEACHERS = 'TEACHERS',
    /** Lehrkraft-Bereich (/my-teaching): teacher-only logins only see this area, office logins get the menu item "Mein Unterricht" */
    TEACHER_PORTAL = 'TEACHER_PORTAL',
    /** Bewerbungs-Bereich (/my-application) of applicants */
    APPLICANT_PORTAL = 'APPLICANT_PORTAL',
    TERMINATIONS = 'TERMINATIONS',
    TODAY = 'TODAY',
    TODOS = 'TODOS',
    TOOLS_BOOK_INVOICE_GENERATOR = 'TOOLS_BOOK_INVOICE_GENERATOR',
    TOOLS_META_ADS = 'META_ADS',
    TOOLS_MIGRATION = 'TOOLS_MIGRATION',
    TOOLS_MISSING_DOCUMENTS = 'TOOLS_MISSING_DOCUMENTS',
    TOOLS_PLACETEL_CALL_CENTER_SCHEDULE = 'TOOLS_PLACETEL_CALL_CENTER_SCHEDULE',
    TOOLS_PLACETEL_TEST_PROFILE = 'TOOLS_PLACETEL_TEST_PROFILE',
    TOOLS_SEPA = 'TOOLS_SEPA',
    TOOLS_SURVEYS = 'TOOLS_SURVEYS',
    TOOLS_TAGS = 'TOOLS_TAGS',
    TOOLS_WORKFLOWS = 'TOOLS_WORKFLOWS',
    TOOLS_EARLY_WARNING = 'TOOLS_EARLY_WARNING',
    TRAININGS = 'TRAININGS',
    VACATIONS = 'VACATIONS',
    VAPI = 'VAPI',
    PARTNER_TODAY = 'PARTNER_TODAY',
    PARTNER_PIPELINE = 'PARTNER_PIPELINE',
    PARTNER_CALL = 'PARTNER_CALL',
    PARTNER_APPOINTMENTS = 'PARTNER_APPOINTMENTS',
    PARTNER_STATS = 'PARTNER_STATS',
    ACQUISITION_BOARD = 'ACQUISITION_BOARD',
    SALES_APPOINTMENTS = 'SALES_APPOINTMENTS',
    SALES_PARTNER_STATS = 'SALES_PARTNER_STATS',
    SALES_PARTNER_CHANGES = 'SALES_PARTNER_CHANGES',
    SALES_PARTNER_SETTINGS = 'SALES_PARTNER_SETTINGS',
    WAREHOUSE_SCHEDULED_PRODUCT_SERIES_SUBSCRIPTIONS = 'WAREHOUSE_SCHEDULED_PRODUCT_SERIES_SUBSCRIPTIONS',
    WHATSAPP_TEMPLATES = 'WHATSAPP_TEMPLATES',
}

export namespace NavigationDrawerAccess {
    type Data = {
        text: string;
        icon: string;
        to: string | (() => string | null);
        group?: NavigationDrawerGroups;
        badge?: {
            color: string;
            value: string | number | (() => string | number | null | undefined);
            description?: string | number | (() => string | number | null | undefined);
        }
        /** Additional right besides the granted menu key (menu entry and route are hidden / blocked without it) */
        requires?: () => boolean;
    }

    export const Record: Record<NavigationDrawerAccess, Data> = {
        [NavigationDrawerAccess.PARTNER_TODAY]: {
            text: 'Heute',
            icon: 'mdi-view-dashboard-outline',
            to: '/partner',
        },
        [NavigationDrawerAccess.PARTNER_PIPELINE]: {
            text: 'Pipeline',
            icon: 'mdi-view-column-outline',
            to: '/partner/pipeline',
        },
        [NavigationDrawerAccess.PARTNER_CALL]: {
            text: 'Telefonieren',
            icon: 'mdi-phone-outline',
            to: '/partner/call',
        },
        [NavigationDrawerAccess.PARTNER_APPOINTMENTS]: {
            text: 'Termine',
            icon: 'mdi-calendar-check-outline',
            to: '/partner/appointments',
        },
        [NavigationDrawerAccess.PARTNER_STATS]: {
            text: 'Leistung',
            icon: 'mdi-chart-box-outline',
            to: '/partner/stats',
        },
        [NavigationDrawerAccess.DASHBOARD]: {
            text: 'Dashboard',
            icon: 'mdi-view-dashboard',
            to: '/',
        },
        [NavigationDrawerAccess.TODOS]: {
            text: 'To-Dos',
            icon: 'mdi-format-list-checks',
            to: '/todos',
        },
        [NavigationDrawerAccess.TODAY]: {
            text: 'Heute',
            icon: 'mdi-calendar-today',
            to: '/today',
        },
        [NavigationDrawerAccess.TEACHER_PORTAL]: {
            text: 'Mein Unterricht',
            icon: 'mdi-human-male-board',
            to: '/my-teaching/today',
        },
        [NavigationDrawerAccess.APPLICANT_PORTAL]: {
            text: 'Meine Bewerbung',
            icon: 'mdi-account-tie-voice-outline',
            to: '/my-application',
        },
        [NavigationDrawerAccess.MY_SCHEDULE]: {
            text: 'Mein Stundenplan',
            icon: 'mdi-clock-outline',
            to: '/schedule',
        },
        [NavigationDrawerAccess.MY_PROFILE]: {
            text: 'Mein Profil',
            icon: 'mdi-account',
            to() {
                if (!store.state.userType || !store.state.user) {
                    return null;
                }
                switch (store.state.userType) {
                    case LoginTypes.EMPLOYEE:
                        return `/employees/${store.state.user._id}`;
                    case LoginTypes.STUDENT:
                        return `/students/${store.state.user._id}`;
                    case LoginTypes.PARENT:
                        return `/parents/${store.state.user._id}`;
                    case LoginTypes.PARTNER:
                        return '/partner';
                    default:
                        return null;
                }
            },
        },
        [NavigationDrawerAccess.PAYOFF]: {
            text: 'Abrechnung',
            icon: 'mdi-account-cash',
            to: '/payoff',
        },
        // @todo: handle /my-appointments
        [NavigationDrawerAccess.MY_APPOINTMENTS]: {
            text: 'Meine Termine',
            icon: 'mdi-calendar-clock',
            to: '/my-appointments',
        },
        [NavigationDrawerAccess.COURSES]: {
            text: 'Alle Kurse',
            icon: 'mdi-table-account',
            to: '/courses',
            group: NavigationDrawerGroups.EVENTS,
        },
        [NavigationDrawerAccess.SUBSTITUTION_PLAN]: {
            text: 'Vertretungsplan',
            icon: 'mdi-account-switch',
            to: '/substitution-plan',
            group: NavigationDrawerGroups.EVENTS,
        },
        [NavigationDrawerAccess.SUBSTITUTION_INQUIRIES]: {
            text: 'Mögliche Vertretungen',
            icon: 'mdi-account-switch-outline',
            to: '/substitution-inquiries',
        },
        [NavigationDrawerAccess.MAKEUP_ROUNDS]: {
            text: 'Nachholrunden online',
            icon: 'mdi-laptop-account',
            to: '/makeup-rounds',
            group: NavigationDrawerGroups.EVENTS,
            badge: {
                color: 'warning',
                value: () => store.state.waitingMakeupOffers,
                description() {
                    const waiting = store.state.waitingMakeupOffers;
                    return waiting ? `${waiting} ${waiting === 1 ? 'Kind wartet' : 'Kinder warten'} auf ein Online-Nachholangebot.` : null;
                },
            },
        },
        [NavigationDrawerAccess.SUBSTITUTION_INQUIRIES_TABLE]: {
            text: 'Vertretungen',
            icon: 'mdi-account-search',
            to: '/substitution-inquiries-table',
            group: NavigationDrawerGroups.EVENTS,
        },
        [NavigationDrawerAccess.TAKEOVER_OFFERS]: {
            text: 'Übernahme-Angebote',
            icon: 'mdi-account-convert-outline',
            to: '/takeover-offers',
            group: NavigationDrawerGroups.EVENTS,
        },
        [NavigationDrawerAccess.INSTITUTIONS]: {
            text: 'Standorte',
            icon: 'mdi-domain',
            to: '/institutions',
            group: NavigationDrawerGroups.PROFILES,
        },
        [NavigationDrawerAccess.STUDENTS]: {
            text: 'Schüler:innen',
            icon: 'mdi-school',
            to: '/students',
            group: NavigationDrawerGroups.PROFILES,
        },
        [NavigationDrawerAccess.PARENTS]: {
            text: 'Eltern',
            icon: 'mdi-account-child',
            to: '/parents',
            group: NavigationDrawerGroups.PROFILES,
        },
        [NavigationDrawerAccess.EMPLOYEES]: {
            text: 'Mitarbeitende',
            icon: 'mdi-account-hard-hat',
            to: '/employees',
            group: NavigationDrawerGroups.PROFILES,
        },
        [NavigationDrawerAccess.TEACHERS]: {
            text: 'Lehrkräfte',
            icon: 'mdi-human-male-board',
            to: '/teachers',
            group: NavigationDrawerGroups.PROFILES,
        },
        [NavigationDrawerAccess.CATALOGUE_PRODUCTS]: {
            text: 'Produkte',
            icon: 'mdi-shopping',
            to: '/catalogue/products',
            group: NavigationDrawerGroups.CATALOGUE,
        },
        [NavigationDrawerAccess.CATALOGUE_SCHEDULED_PRODUCT_SERIES]: {
            text: 'Serienversand',
            icon: 'mdi-receipt-text-clock',
            to: '/catalogue/scheduled-product-series',
            group: NavigationDrawerGroups.CATALOGUE,
        },
        [NavigationDrawerAccess.QUALITY_MANAGEMENT_COCKPIT]: {
            text: 'QM-Cockpit',
            icon: 'mdi-shield-check-outline',
            to: '/quality-management',
            group: NavigationDrawerGroups.QUALITY_MANAGEMENT,
        },
        // Old keys: only grant the cockpit (withImpliedAccess), never an own entry
        [NavigationDrawerAccess.QUALITY_MANAGEMENT_TEACHERS]: {
            text: 'QM-Cockpit (früher „Lehrkräfte – QM“)',
            icon: 'mdi-human-male-board-poll',
            to: '/quality-management',
            group: NavigationDrawerGroups.QUALITY_MANAGEMENT,
            requires: () => false,
        },
        [NavigationDrawerAccess.QUALITY_MANAGEMENT_INSTITUTIONS]: {
            text: 'QM-Cockpit (früher „Standorte – QM“)',
            icon: 'mdi-office-building',
            to: '/quality-management',
            group: NavigationDrawerGroups.QUALITY_MANAGEMENT,
            requires: () => false,
        },
        [NavigationDrawerAccess.ACQUISITION_LIST_OVERVIEW]: {
            text: 'Übersicht',
            icon: 'mdi-list-box',
            to: '/acquisitionListsOverview',
            group: NavigationDrawerGroups.ACQUISITION_LISTS,
        },
        [NavigationDrawerAccess.ACQUISITION_LISTS_TABLE]: {
            text: 'Alle Listen',
            icon: 'mdi-list-status',
            to: '/acquisitionLists',
            group: NavigationDrawerGroups.ACQUISITION_LISTS,
        },
        [NavigationDrawerAccess.ACQUISITION_BOARD]: {
            text: 'Akquise-Board',
            icon: 'mdi-view-column-outline',
            to: '/acquisitionBoard',
            group: NavigationDrawerGroups.ACQUISITION_LISTS,
        },
        [NavigationDrawerAccess.SALES_APPOINTMENTS]: {
            text: 'Folgegespräche',
            icon: 'mdi-calendar-account-outline',
            to: '/salesAppointments',
            group: NavigationDrawerGroups.ACQUISITION_LISTS,
        },
        [NavigationDrawerAccess.SALES_PARTNER_STATS]: {
            text: 'Auswertung Agentur',
            icon: 'mdi-chart-timeline-variant',
            to: '/salesPartner/stats',
            group: NavigationDrawerGroups.ACQUISITION_LISTS,
        },
        [NavigationDrawerAccess.SALES_PARTNER_CHANGES]: {
            text: 'Agentur-Änderungen',
            icon: 'mdi-file-compare',
            to: '/salesPartner/changes',
            group: NavigationDrawerGroups.ACQUISITION_LISTS,
        },
        [NavigationDrawerAccess.SALES_PARTNER_SETTINGS]: {
            text: 'Agentur verwalten',
            icon: 'mdi-account-tie-outline',
            to: '/salesPartner/settings',
            group: NavigationDrawerGroups.ACQUISITION_LISTS,
        },
        [NavigationDrawerAccess.STATS]: {
            text: 'Statistiken',
            icon: 'mdi-chart-line',
            to: '/stats',
        },
        [NavigationDrawerAccess.PLANNING_HANDOUT_EVENTS]: {
            text: 'Austeilaktionen',
            icon: 'mdi-text-box-check-outline',
            to: '/planning/handout-events',
            group: NavigationDrawerGroups.PLANNING,
        },
        [NavigationDrawerAccess.PLANNING_HANDOUT_OVERVIEW]: {
            text: 'Terminübersicht',
            icon: 'mdi-calendar-alert',
            to: '/planning/handout-overview',
            group: NavigationDrawerGroups.PLANNING,
        },
        [NavigationDrawerAccess.PLANNING_HANDOUT_FEEDBACK]: {
            text: 'Feedback',
            icon: 'mdi-comment-alert',
            to: '/planning/handout-events-feedback',
            group: NavigationDrawerGroups.PLANNING,
        },
        [NavigationDrawerAccess.PLANNING_OPEN_INSTITUTIONS]: {
            text: 'Offene Standorte',
            icon: 'mdi-map-marker-alert',
            to: '/planning/open-institutions',
            group: NavigationDrawerGroups.PLANNING,
        },
        [NavigationDrawerAccess.APPLICANTS_LANDING_PAGE_OVERVIEW]: {
            text: 'Landingpage',
            icon: 'mdi-remote-desktop',
            to: '/recruiter/landingpage',
            group: NavigationDrawerGroups.JOB_ADS,
        },
        [NavigationDrawerAccess.JOB_ADS_OVERVIEW]: {
            text: 'Stellenanzeigen',
            icon: 'mdi-newspaper-variant-outline',
            to: '/recruiter/jobAds',
            group: NavigationDrawerGroups.JOB_ADS,
        },
        [NavigationDrawerAccess.JOB_ADS_APPLICANTS]: {
            text: 'Bewerber:innen',
            icon: 'mdi-account-tie-voice',
            to: '/recruiter/applicants',
            group: NavigationDrawerGroups.JOB_ADS,
        },
        [NavigationDrawerAccess.RECRUITING_OVERVIEW]: {
            text: 'Auswertung',
            icon: 'mdi-chart-box-outline',
            to: '/recruiter/overview',
            group: NavigationDrawerGroups.JOB_ADS,
            // read right on ALL applications – checked again by the stat-manager; teachers and single hooks never have it
            requires: () => canReadRecruitingOverview(),
        },
        [NavigationDrawerAccess.INVOICE_TEMPLATES]: {
            text: 'Rechnungsvorlagen',
            icon: 'mdi-clipboard-account',
            to: '/templates/invoice',
            group: NavigationDrawerGroups.TEMPLATES,
        },
        [NavigationDrawerAccess.EMPLOYEE_CONTRACT_TEMPLATES]: {
            text: 'Vergütungsverträge',
            icon: 'mdi-file-document-edit',
            to: '/templates/contract-templates',
            group: NavigationDrawerGroups.TEMPLATES,
        },
        [NavigationDrawerAccess.EMAIL_TEMPLATES]: {
            text: 'E-Mail Vorlagen',
            icon: 'mdi-email-edit',
            to: '/templates/mail',
            group: NavigationDrawerGroups.TEMPLATES,
        },
        [NavigationDrawerAccess.WHATSAPP_TEMPLATES]: {
            text: 'WhatsApp Vorlagen',
            icon: 'mdi-whatsapp',
            to: '/templates/whatsapp',
            group: NavigationDrawerGroups.TEMPLATES,
        },
        [NavigationDrawerAccess.BOOKING_FORM_TEMPLATES]: {
            text: 'Anmeldevorlagen',
            icon: 'mdi-file-document-edit',
            to: '/booking',
            group: NavigationDrawerGroups.TEMPLATES,
        },
        [NavigationDrawerAccess.ACTIVITY_REGISTRATIONS]: {
            text: 'Anmeldungen',
            icon: 'mdi-cart-arrow-down',
            to: '/activity-registrations',
            badge: {
                color: 'success',
                value: () => store.state.openSubmissions,
                description() {
                    if (store.state.openSubmissions) {
                        const openSubmissions = store.state.openSubmissions;
                        return `Derzeit gibt es ${openSubmissions} ${openSubmissions === 1 ? 'Anmeldung' : 'Anmeldungen'}.`;
                    }
                    return undefined;
                },
            },
        },
        [NavigationDrawerAccess.INVOICE_CYCLES]: {
            text: 'Rechnungsläufe',
            icon: 'mdi-cash-sync',
            to: '/accounting/invoice-cycles',
            group: NavigationDrawerGroups.ACCOUNTING,
        },
        [NavigationDrawerAccess.INVOICES]: {
            text: 'Ausgangsrechnungen',
            icon: 'mdi-invoice-arrow-right',
            to: '/accounting/invoices',
            group: NavigationDrawerGroups.ACCOUNTING,
        },
        [NavigationDrawerAccess.ROOM_RENT]: {
            text: 'Raummiete',
            icon: 'mdi-door',
            to: '/accounting/room-rent',
            group: NavigationDrawerGroups.ACCOUNTING,
            // read right on all rent contracts incl. versions (amounts) – checked again by the stat-manager; teachers never have it
            requires: () => {
                const cam = store.state.crudAccessManager;
                return !!cam && cam.getAccessibleEntities(CrudEntityTypes.RENT_CONTRACT, CrudActionTypes.READ) === 'all'
                    && cam.canReadAllProvided(CrudEntityTypes.RENT_CONTRACT, 'versions');
            },
        },
        [NavigationDrawerAccess.MAKEUP_REFUNDS]: {
            text: 'Rückerstattungen',
            icon: 'mdi-cash-refund',
            to: '/accounting/refunds',
            group: NavigationDrawerGroups.ACCOUNTING,
            // read rule of the refund claims (substitution-manager) + payout right of Rückerstattung v2 (accountant)
            requires: () => {
                const cam = store.state.crudAccessManager;
                return !!cam && (cam.getCustom(CustomAccessKeys.MANAGE_MAKEUP_REFUNDS) === true
                    || cam.getCustom(CustomAccessKeys.PAYOUT_MAKEUP_REFUNDS) === true
                    || cam.getCustom(CustomAccessKeys.MANAGE_MAKEUP_CLAIMS) === true
                    || cam.canAccessType(CrudEntityTypes.SUBSTITUTION_INQUIRY, CrudActionTypes.UPDATE));
            },
        },
        [NavigationDrawerAccess.PAYOFF_AUDIT]: {
            text: 'Rechnungsprüfung',
            icon: 'mdi-cash-check',
            to: '/payoff-audit',
            group: NavigationDrawerGroups.ACCOUNTING,
            badge: {
                color: 'error',
                value: () => store.state.pendingPaychecksCount,
                description() {
                    if (store.state.pendingPaychecksCount) {
                        const pendingPaychecksCount = store.state.pendingPaychecksCount;
                        return `Derzeit gibt es ${pendingPaychecksCount} zu prüfende ${pendingPaychecksCount === 1 ? 'Rechnung' : 'Rechnungen'}.`;
                    }
                    return undefined;
                },
            },
        },
        [NavigationDrawerAccess.PRODUCT_SHIPMENT_BUNDLES]: {
            text: 'Versandpakete',
            icon: 'mdi-package-variant',
            to: '/warehouse/shipment-bundles',
            group: NavigationDrawerGroups.WAREHOUSE,
        },
        [NavigationDrawerAccess.PRODUCT_ORDERS]: {
            text: 'Versand',
            icon: 'mdi-truck-fast-outline',
            to: '/warehouse/shipping',
            group: NavigationDrawerGroups.WAREHOUSE,
            badge: {
                color: 'success',
                value: () => store.state.openOrders,
                description() {
                    if (store.state.openOrders) {
                        const openOrders = store.state.openOrders;
                        return `Derzeit gibt es ${openOrders} offene ${openOrders === 1 ? 'Bestellung' : 'Bestellungen'}.`;
                    }
                    return undefined;
                },
            },
        },
        // Old key: "Sendungen" is a tab of "Versand"; grants the Versand entry (withImpliedAccess), never an own entry
        [NavigationDrawerAccess.PRODUCT_SHIPMENTS]: {
            text: 'Versand › Sendungen (früher eigener Menüpunkt)',
            icon: 'mdi-package-variant',
            to: '/warehouse/shipping?tab=shipments',
            group: NavigationDrawerGroups.WAREHOUSE,
            requires: () => false,
        },
        [NavigationDrawerAccess.WAREHOUSE_SCHEDULED_PRODUCT_SERIES_SUBSCRIPTIONS]: {
            text: 'Serienversand-Abos',
            icon: 'mdi-archive-sync',
            to: '/warehouse/scheduled-product-series',
            group: NavigationDrawerGroups.WAREHOUSE,
        },
        [NavigationDrawerAccess.TERMINATIONS]: {
            text: 'Kündigungen',
            icon: 'mdi-cancel',
            to: '/terminations',
            badge: {
                color: 'error',
                value: () => store.state.openTerminations,
                description() {
                    if (store.state.openTerminations) {
                        const openTerminations = store.state.openTerminations;
                        return `Bis Monatsende ${openTerminations === 1 ? 'wird 1 Kündigung' : `werden ${openTerminations} Kündigungen`} wirksam.`;
                    }
                    return undefined;
                },
            },
        },
        [NavigationDrawerAccess.TRAININGS]: {
            text: 'Schulungen',
            icon: 'mdi-cast-education',
            to: '/trainings',
            group: NavigationDrawerGroups.EVENTS,
        },
        [NavigationDrawerAccess.TOOLS_META_ADS]: {
            text: 'Meta Ads',
            icon: 'mdi-facebook',
            to: '/tools/metaAds',
            group: NavigationDrawerGroups.TOOLS,
        },
        [NavigationDrawerAccess.TOOLS_MISSING_DOCUMENTS]: {
            text: 'Fehlende Dokumente',
            icon: 'mdi-file-cancel',
            to: '/tools/missingDocuments',
            group: NavigationDrawerGroups.TOOLS,
        },
        [NavigationDrawerAccess.TOOLS_SURVEYS]: {
            text: 'Umfragen',
            icon: 'mdi-poll',
            to: '/tools/surveys',
            group: NavigationDrawerGroups.TOOLS,
        },
        [NavigationDrawerAccess.TOOLS_WORKFLOWS]: {
            text: 'Workflows',
            icon: 'mdi-cogs',
            to: '/tools/workflows',
            group: NavigationDrawerGroups.TOOLS,
        },
        // Old key: the early warning lives in the QM-Cockpit; grants the cockpit route, no own menu entry
        [NavigationDrawerAccess.TOOLS_EARLY_WARNING]: {
            text: 'QM-Cockpit › Frühwarnung (früher Werkzeuge)',
            icon: 'mdi-bell-alert-outline',
            to: '/quality-management?tab=earlyWarning',
            group: NavigationDrawerGroups.QUALITY_MANAGEMENT,
            requires: () => false,
        },
        [NavigationDrawerAccess.TOOLS_TAGS]: {
            text: 'Tags',
            icon: 'mdi-tag',
            to: '/tools/tags',
            group: NavigationDrawerGroups.TOOLS,
        },
        [NavigationDrawerAccess.TOOLS_SEPA]: {
            text: 'SEPA',
            icon: 'mdi-bank-transfer-out',
            to: '/tools/sepaTools',
            group: NavigationDrawerGroups.TOOLS,
        },
        [NavigationDrawerAccess.TOOLS_BOOK_INVOICE_GENERATOR]: {
            text: 'Buchrechner',
            icon: 'mdi-book-cog',
            to: '/tools/bookInvoiceGenerator',
            group: NavigationDrawerGroups.TOOLS,
        },
        [NavigationDrawerAccess.TOOLS_MIGRATION]: {
            text: 'Migration',
            icon: 'mdi-database-refresh',
            to: '/tools/migration',
            group: NavigationDrawerGroups.TOOLS,
        },
        [NavigationDrawerAccess.TOOLS_PLACETEL_TEST_PROFILE]: {
            text: 'Placetel-Testprofil',
            icon: 'mdi-phone-check',
            to: '/tools/placetelTestProfile',
            group: NavigationDrawerGroups.TOOLS,
        },
        [NavigationDrawerAccess.TOOLS_PLACETEL_CALL_CENTER_SCHEDULE]: {
            text: 'Call-Center-Dienstplan',
            icon: 'mdi-calendar-account',
            to: '/tools/placetelCallCenterSchedule',
            group: NavigationDrawerGroups.TOOLS,
        },
        [NavigationDrawerAccess.VAPI]: {
            text: 'Vapi',
            icon: 'mdi-alpha-v',
            to: '/vapi',
            group: NavigationDrawerGroups.TOOLS,
        },
        [NavigationDrawerAccess.SUBJECTS]: {
            text: 'Fächer',
            icon: 'mdi-book',
            to: '/subjects',
            group: NavigationDrawerGroups.OTHER,
        },
        [NavigationDrawerAccess.ACTIVITIES]: {
            text: 'Aktivitäten',
            icon: 'mdi-ticket',
            to: '/activities',
            group: NavigationDrawerGroups.OTHER,
        },
        [NavigationDrawerAccess.VACATIONS]: {
            text: 'Ferien & Feiertage',
            icon: 'mdi-beach',
            to: '/vacation',
            group: NavigationDrawerGroups.OTHER,
        },
        [NavigationDrawerAccess.TAXONOMIES]: {
            text: 'Taxonomien',
            icon: 'mdi-format-list-bulleted',
            to: '/taxonomies',
            group: NavigationDrawerGroups.OTHER,
        },
        // [NavigationDrawerAccess.OPTIMIZATIONS]: {
        //     text: 'Optimierungen',
        //     icon: 'mdi-calculator-variant',
        //     to: '/optimizations',
        // },
        [NavigationDrawerAccess.SETTINGS]: {
            text: 'Einstellungen',
            icon: 'mdi-wrench',
            to: '/settings',
        },
    }

    export const Items = mapRecordToItems(Record);

    export type DrawerGroupItem = {
        type: 'group',
        group: NavigationDrawerGroups,
        text: string,
        icon: string,
        items: DrawerItem[],
    };

    export type DrawerDataItem = { type: 'item' } & Data;

    export type DrawerItem = DrawerGroupItem | DrawerDataItem;

    export const PARTNER_ITEMS: NavigationDrawerAccess[] = [
        NavigationDrawerAccess.PARTNER_TODAY,
        NavigationDrawerAccess.PARTNER_PIPELINE,
        NavigationDrawerAccess.PARTNER_CALL,
        NavigationDrawerAccess.PARTNER_APPOINTMENTS,
        NavigationDrawerAccess.PARTNER_STATS,
    ];

    /** Whether the additional right of a menu key (`requires`) is given; keys without it are always available */
    export const isAvailable = (key: NavigationDrawerAccess): boolean => {
        const requires = Record[key]?.requires;
        return !requires || requires();
    };

    /** The old menu keys ("Lehrkräfte – QM", "Standorte – QM", "Frühwarnung") grant the QM-Cockpit that replaces these pages */
    const withQmCockpit = (access: NavigationDrawerAccess[]): NavigationDrawerAccess[] => {
        if (access.includes(NavigationDrawerAccess.QUALITY_MANAGEMENT_COCKPIT)
            || !(access.includes(NavigationDrawerAccess.QUALITY_MANAGEMENT_TEACHERS) || access.includes(NavigationDrawerAccess.QUALITY_MANAGEMENT_INSTITUTIONS)
                || access.includes(NavigationDrawerAccess.TOOLS_EARLY_WARNING))) {
            return access;
        }
        return [...access, NavigationDrawerAccess.QUALITY_MANAGEMENT_COCKPIT];
    };

    /**
     * "Übernahme-Angebote" belongs to the substitution inquiries: shown next to "Vertretungsanfragen" for everybody who has
     * that menu item and read access to the substitution inquiries (the offers use the same permission), no new role needed.
     */
    /** The old menu key "Sendungen" grants "Versand" (the shipments are a tab there) */
    const withShipping = (access: NavigationDrawerAccess[]): NavigationDrawerAccess[] => {
        if (access.includes(NavigationDrawerAccess.PRODUCT_ORDERS) || !access.includes(NavigationDrawerAccess.PRODUCT_SHIPMENTS)) {
            return access;
        }
        return [...access, NavigationDrawerAccess.PRODUCT_ORDERS];
    };

    /** „Bewerber:innen“ + Leserecht auf alle Bewerbungen bringt die Recruiting-Auswertung mit, keine neue Rolle nötig */
    const withRecruitingOverview = (access: NavigationDrawerAccess[]): NavigationDrawerAccess[] => {
        if (access.includes(NavigationDrawerAccess.RECRUITING_OVERVIEW) || !access.includes(NavigationDrawerAccess.JOB_ADS_APPLICANTS)) {
            return access;
        }
        return [...access, NavigationDrawerAccess.RECRUITING_OVERVIEW];
    };

    const withImpliedAccess = (raw: NavigationDrawerAccess[]): NavigationDrawerAccess[] => {
        const access = withRecruitingOverview(withShipping(withQmCockpit(raw)));
        if (access.includes(NavigationDrawerAccess.TAKEOVER_OFFERS) || !access.includes(NavigationDrawerAccess.SUBSTITUTION_INQUIRIES_TABLE)
            || !store.state.crudAccessManager?.canAccessType(CrudEntityTypes.SUBSTITUTION_INQUIRY, CrudActionTypes.READ)) {
            return access;
        }
        return [...access, NavigationDrawerAccess.TAKEOVER_OFFERS];
    };

    /**
     * Partner logins always get the partner area, no role is needed for it.
     * The sales-partner service only answers partner requests for active partners and their own lists.
     */
    export const getEffectiveAccess = (access: NavigationDrawerAccess[]): NavigationDrawerAccess[] => {
        if (store.state.userType !== LoginTypes.PARTNER) {
            return withImpliedAccess(access).filter(isAvailable);
        }
        return Array.from(new Set([...access, ...PARTNER_ITEMS])).filter(isAvailable);
    };

    /** Employees can only "view as agency" with read access to the sales partners (the view needs a partner to show) */
    export const canViewAsAgency = (): boolean => !!store.state.crudAccessManager?.canAccessType(CrudEntityTypes.SALES_PARTNER, CrudActionTypes.READ);

    export const getDrawerItems = (access: NavigationDrawerAccess[]): DrawerItem[] => {
        // The partner area is only for partner logins, and partners only see the partner area
        const isPartner = store.state.userType === LoginTypes.PARTNER;
        const agencyView = !isPartner && canViewAsAgency();
        const effectiveAccess = getEffectiveAccess(access || []).filter((item) => isPartner || agencyView || !PARTNER_ITEMS.includes(item));
        // Employees see the partner area as read-only "view as agency" group at the end of the menu
        const accessibleItems = Items
            .filter(item => effectiveAccess.includes(item.value) && (isPartner ? PARTNER_ITEMS.includes(item.value) : true))
            .map(item => !isPartner && PARTNER_ITEMS.includes(item.value) ? {...item, group: NavigationDrawerGroups.AGENCY_VIEW} : item)
            .sort((a, b) => Number(!isPartner && PARTNER_ITEMS.includes(a.value)) - Number(!isPartner && PARTNER_ITEMS.includes(b.value)));

        const result: DrawerItem[] = [];
        for (const item of accessibleItems) {
            if (item.group) {
                const group = result.find(i => i.type === 'group' && i.group === item.group);
                if (group?.type === 'group') {
                    group.items.push({type: 'item', ...item});
                } else {
                    result.push({
                        type: 'group',
                        group: item.group,
                        ...NavigationDrawerGroups.Record[item.group],
                        items: [{type: 'item', ...item}],
                    });
                }
            } else {
                result.push({type: 'item', ...item});
            }
        }

        return result;
    }
}
